import Component from '@glimmer/component';
import { concat } from '@ember/helper';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import style from 'ember-style-modifier/modifiers/style';
import { PhRocketLaunch, PhTrendUp, PhSparkle, PhConfetti } from 'ember-phosphor-icons';

/**
 * DebtJourneyHero - Motivational hero banner showing debt freedom progress
 *
 * Displays a progress bar toward debt freedom with encouraging messaging
 * and key milestone indicators. Designed to make debt repayment feel
 * achievable and celebratory.
 *
 * @example
 * ```gts
 * <DebtJourneyHero
 *   @currentBalance={{45230}}
 *   @originalBalance={{68500}}
 *   @estimatedPayoffDate="2029-08-15"
 *   @yearProgressAmount={{2340}}
 * />
 * ```
 */
interface DebtJourneyHeroSignature {
  Args: {
    /** Current total loan balance */
    currentBalance: number;
    /** Original total loan balance (for progress calculation) */
    originalBalance: number;
    /** Estimated debt-free date */
    estimatedPayoffDate: string;
    /** Amount paid down this year */
    yearProgressAmount: number;
    /** Optional: percentage improvement vs last year */
    yearOverYearImprovement?: number;
  };
  Element: HTMLDivElement;
}

export default class DebtJourneyHero extends Component<DebtJourneyHeroSignature> {
  get progressPercent() {
    const paid = this.args.originalBalance - this.args.currentBalance;
    return Math.round((paid / this.args.originalBalance) * 100);
  }

  get progressWidth() {
    return `${this.progressPercent}%`;
  }

  get formattedBalance() {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(this.args.currentBalance);
  }

  get formattedYearProgress() {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(this.args.yearProgressAmount);
  }

  get formattedPayoffDate() {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric',
    }).format(new Date(this.args.estimatedPayoffDate));
  }

  get motivationalMessage() {
    if (this.progressPercent >= 75) {
      return "You're in the home stretch! Debt freedom is within reach.";
    }
    if (this.progressPercent >= 50) {
      return 'Halfway there! Your dedication is paying off.';
    }
    if (this.progressPercent >= 25) {
      return 'Great momentum! Every payment brings you closer.';
    }
    return 'Your debt freedom journey has begun. Keep going!';
  }

  get iconComponent() {
    if (this.progressPercent >= 75) {
      return PhConfetti;
    }
    if (this.progressPercent >= 50) {
      return PhSparkle;
    }
    return PhRocketLaunch;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class="tio-debt-journey" ...attributes>
      {{! Background decoration - subtle floating circles }}
      <div class="tio-debt-journey__orb tio-debt-journey__orb--large hero-float"></div>
      <div
        class="tio-debt-journey__orb tio-debt-journey__orb--small hero-float"
        {{style animation-delay="-18s"}}
      ></div>
      <div
        class="tio-debt-journey__orb tio-debt-journey__orb--medium hero-float"
        {{style animation-delay="-35s"}}
      ></div>

      {{! Header }}
      <div class="tio-debt-journey__header">
        <div class="tio-row">
          {{#let this.iconComponent as |Icon|}}
            <div class="tio-debt-journey__icon">
              <Icon @size={{28}} weight="duotone" />
            </div>
          {{/let}}
          <div>
            <Heading @level={{1}} class="tio-debt-journey__title">
              Your Debt Freedom Journey
            </Heading>
            <Text class="tio-debt-journey__subtitle">{{this.motivationalMessage}}</Text>
          </div>
        </div>
        {{#if @yearOverYearImprovement}}
          <div class="tio-debt-journey__improvement">
            <PhTrendUp @size={{16}} />
            <span>{{@yearOverYearImprovement}}% faster than last year</span>
          </div>
        {{/if}}
      </div>

      {{! Progress Section }}
      <div class="tio-debt-journey__progress">
        {{! Progress bar background }}
        <div class="tio-debt-journey__progress-track">
          {{! Progress bar fill }}
          <div
            class="tio-debt-journey__progress-fill"
            {{style
              width=(concat this.progressPercent "%")
              background-color="var(--color-ocean-400)"
            }}
          ></div>
        </div>

        {{! Progress labels }}
        <div class="tio-debt-journey__progress-labels">
          <span class="tio-debt-journey__muted">{{this.formattedBalance}} remaining</span>
          <span class="tio-debt-journey__progress-value">{{this.progressPercent}}% paid off</span>
          <span class="tio-debt-journey__muted">$0 by {{this.formattedPayoffDate}}</span>
        </div>
      </div>

      {{! Stats Row }}
      <div class="tio-debt-journey__stats">
        <div class="tio-debt-journey__stat">
          <div class="tio-text-2xl-bold">{{this.formattedYearProgress}}</div>
          <Text @size="sm" class="tio-debt-journey__muted">Paid this year</Text>
        </div>
        <div class="tio-debt-journey__stat tio-debt-journey__stat--divided">
          <div class="tio-text-2xl-bold">{{this.formattedPayoffDate}}</div>
          <Text @size="sm" class="tio-debt-journey__muted">Debt-free date</Text>
        </div>
        <div class="tio-debt-journey__stat">
          <div class="tio-text-2xl-bold">{{this.progressPercent}}%</div>
          <Text @size="sm" class="tio-debt-journey__muted">Progress</Text>
        </div>
      </div>
    </div>
  </template>
}
